import React from 'react';
import { SEO } from '@/components/SEO';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import LocationFAQ from '@/components/locations/LocationFAQ';
import LocationNeighborhoods from '@/components/locations/LocationNeighborhoods';
import LocationBlogLinks from '@/components/locations/LocationBlogLinks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, MapPin, Star, Droplets, Sparkles, Wind, Filter, Brush, Sun } from 'lucide-react';

const OxnardPage = () => {
  const navigate = useNavigate();
  
  const locationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Ventura Power Washing - Oxnard",
    "description": "Professional pressure washing, window cleaning, and carpet care services in Oxnard, CA. Eco-friendly cleaning solutions for residential and commercial properties.",
    "url": "https://www.venturapowerwashing.com/oxnard",
    "telephone": "(805) 464-7166",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Oxnard",
      "addressRegion": "CA",
      "postalCode": "93030",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "34.1975",
      "longitude": "-119.1771"
    },
    "areaServed": {
      "@type": "City",
      "name": "Oxnard",
      "containedInPlace": {
        "@type": "State",
        "name": "California"
      }
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "07:00",
      "closes": "19:00"
    },
    "priceRange": "$$",
    "image": "https://www.venturapowerwashing.com/og-image.jpg",
    "sameAs": [
      "https://www.facebook.com/venturapowerwashing",
      "https://www.instagram.com/venturacleaningco",
      "https://www.yelp.com/biz/ventura-power-washing-thousand-oaks"
    ]
  };

  const services = [
    {
      icon: <Droplets className="h-8 w-8 text-ventura-blue" />,
      title: "Pressure Washing",
      description: "Professional pressure washing for coastal properties, removing salt buildup.",
      link: "/services/pressure-washing"
    },
    {
      icon: <Sparkles className="h-8 w-8 text-ventura-blue" />,
      title: "Window Cleaning",
      description: "Specialized window cleaning for coastal climate, removing salt spray.",
      link: "/services/window-washing"
    },
    {
      icon: <Wind className="h-8 w-8 text-ventura-blue" />,
      title: "Carpet Cleaning",
      description: "Deep carpet cleaning that handles sand, salt, and moisture.",
      link: "/services/carpet-cleaning"
    },
    {
      icon: <Filter className="h-8 w-8 text-ventura-blue" />,
      title: "Gutter Cleaning",
      description: "Keep gutters clear and protect your home from water damage.",
      link: "/services/gutter-cleaning"
    },
    {
      icon: <Brush className="h-8 w-8 text-ventura-blue" />,
      title: "Soft Wash",
      description: "Gentle cleaning for delicate surfaces like stucco and painted exteriors.",
      link: "/services/soft-wash"
    },
    {
      icon: <Sun className="h-8 w-8 text-ventura-blue" />,
      title: "Solar Panel Cleaning",
      description: "Maximize solar efficiency with professional panel maintenance.",
      link: "/services/solar-panel-cleaning"
    }
  ];

  const faqs = [
    {
      question: "How much does pressure washing cost in Oxnard, CA?",
      answer: "Most driveway, patio, and house pressure washing jobs in Oxnard run $150-$400, depending on square footage and surface type. We provide a free, no-obligation estimate before any work begins."
    },
    {
      question: "How much does window cleaning cost in Oxnard?",
      answer: "Residential window cleaning in Oxnard typically costs $150-$300 for a standard home, inside and out. Coastal homes near Oxnard Shores or Mandalay Bay often need more frequent cleaning due to salt spray."
    },
    {
      question: "Do you offer carpet cleaning in Oxnard?",
      answer: "Yes. Whole-house carpet cleaning in Oxnard generally runs $100-$300 depending on square footage and the number of rooms. We use deep extraction methods that handle sand, salt, and coastal moisture."
    },
    {
      question: "Is solar panel cleaning worth it for Oxnard homes?",
      answer: "Yes — Oxnard's coastal salt air creates a stubborn film on solar panels that can cut efficiency by 15-30%. Professional solar panel cleaning in Oxnard typically costs $150-$350 and helps maximize energy production."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Pressure Washing Oxnard CA | Window & Carpet Cleaning Services"
        description="Professional pressure washing, window cleaning & carpet care in Oxnard, CA. Serving 93030, 93033, 93035, 93036. Coastal cleaning specialists. Free estimates!"
        keywords="pressure washing oxnard, window cleaning oxnard, carpet cleaning oxnard, power washing 93030, house washing oxnard, coastal cleaning"
        path="/oxnard"
      >
        <script type="application/ld+json">
          {JSON.stringify(locationSchema)}
        </script>
      </SEO>

      <Navbar />
      <Breadcrumbs />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-ventura-blue to-ventura-darkBlue text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Professional Pressure Washing Services in Oxnard, CA
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Coastal cleaning specialists for Oxnard residents and businesses in 93030, 93033, 93035, and 93036
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-white text-ventura-blue hover:bg-gray-100 text-lg px-8 py-4"
                onClick={() => window.location.href = "tel:+18054647166"}
              >
                <Phone className="mr-2" size={20} />
                Call (805) 464-7166
              </Button>
              <Button 
                className="bg-ventura-green text-white hover:bg-green-600 text-lg px-8 py-4"
                onClick={() => navigate('/free-estimate')}
              >
                Get Free Oxnard Estimate
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Oxnard Coastal Cleaning Services
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-shadow h-full hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="text-ventura-blue hover:text-ventura-darkBlue font-semibold hover:underline"
                  >
                    Learn More →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area Information */}
      <section className="section-padding bg-ventura-lightGray">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">
              What Makes Oxnard's Coastal Cleaning Challenges Unique?
            </h2>
            <p className="text-lg text-gray-700 text-center mb-8">
              As Ventura County's largest coastal city, Oxnard faces ocean salt spray, beach sand, and marine moisture that
              accelerate corrosion and grime buildup on exterior surfaces. Our specialized coastal cleaning techniques protect
              your property from this damage while maintaining its beauty in Oxnard's challenging marine environment.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-2xl font-bold mb-4">Oxnard Areas We Serve</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Downtown Oxnard (93030)</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Oxnard Shores (93035)</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />River Ridge (93036)</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Seabridge</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Mandalay Bay</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold mb-4">Coastal Cleaning Experts</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Salt Air Corrosion Removal</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Marine Environment Specialists</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Sand & Moisture Cleaning</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Licensed & Insured</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Local Oxnard Team</li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <p className="text-lg text-gray-700 mb-6">
                As Ventura County's largest coastal city, Oxnard faces unique cleaning challenges from ocean salt spray, beach sand, and marine moisture. Our specialized coastal cleaning techniques protect your property from corrosion while maintaining its beauty in this challenging environment.
              </p>
              
              <Button 
                className="button-primary text-lg px-8 py-4"
                onClick={() => navigate('/free-estimate')}
              >
                Get Your Free Oxnard Coastal Cleaning Quote Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LocationNeighborhoods
        city="Oxnard"
        neighborhoods={[
          { name: 'Riverpark', note: 'We serve Riverpark homeowners and HOA communities with driveway and walkway cleaning, soft washing for stucco, and window, gutter, and solar panel cleaning.' },
        ]}
      />
      <LocationFAQ city="Oxnard" faqs={faqs} />
      <LocationBlogLinks city="Oxnard" />

      <Footer />
    </div>
  );
};

export default OxnardPage;